import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, CheckCircle2, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WireframeGlobe } from './WireframeGlobe';
export function Hero() {
  return <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden bg-[#0a0a0a]" data-id="element-75">
      {/* Premium Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" data-id="element-76"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[600px] bg-brand-emerald/15 blur-[120px] rounded-full pointer-events-none" data-id="element-77"></div>
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-emerald/30 to-transparent" data-id="element-78"></div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-79">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-6" data-id="element-80">
          {/* Text Content */}
          <div className="w-full lg:w-1/2" data-id="element-81">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.1,
            ease: 'easeOut'
          }} data-id="element-82">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6 tracking-tighter" data-id="element-83">
                Evoluciona tu negocio hacia un ecosistema digital de{' '}
                <span className="text-gradient-animated" data-id="element-84">alto rendimiento</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-xl leading-relaxed font-medium" data-id="element-85">
                Creamos tu página web, carta digital y perfiles sociales desde
                cero. Un ecosistema digital completo que convierte visitantes en
                clientes fieles.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-86">
                <Link to="/checkout" className="inline-flex justify-center items-center gap-2 bg-brand-emerald text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-brand-emeraldLight transition-all duration-300 shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] hover:-translate-y-1 group" data-id="element-87">
                  Empezar ahora
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-88" />
                </Link>
                <Link to="/proceso" className="inline-flex justify-center items-center gap-2 bg-white/5 text-white border border-white/10 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm group" data-id="element-89">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-90">
                    <Play size={14} className="text-white ml-0.5" data-id="element-91" />
                  </div>
                  Ver cómo funciona
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Wireframe Globe Composition */}
          <div className="w-full lg:w-1/2 relative h-[280px] sm:h-[350px] lg:h-[500px] flex items-center justify-center overflow-hidden" data-id="element-92">
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5,
            ease: 'easeOut',
            delay: 0.2
          }} className="relative w-full h-full flex items-center justify-center" data-id="element-93">
              <WireframeGlobe size={550} className="absolute inset-0 z-10" data-id="element-94" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Pricing / Value Proposition Strip */}
      <div className="container mx-auto px-6 md:px-12 mt-16 relative z-20" data-id="element-95">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="relative rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5 shadow-2xl" data-id="element-96">
          <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-97"></div>
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-emerald/20 to-transparent" data-id="element-98"></div>
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-48 bg-brand-emerald/10 blur-[100px] rounded-full pointer-events-none" data-id="element-99"></div>

          <div className="relative z-10 p-5 sm:p-8 md:p-12 lg:p-16" data-id="element-100">
            {/* Main Content: 3 Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch" data-id="element-101">
              {/* Total Price Card */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="flex flex-col items-center justify-center text-center p-6 sm:p-8 md:p-10 rounded-3xl bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-brand-emerald/30 relative overflow-hidden shadow-[0_0_40px_rgba(16,185,129,0.1)]" data-id="element-102">
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-emerald to-brand-emeraldLight" data-id="element-103"></div>
                <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-104"></div>

                <h3 className="text-xl text-white font-bold mb-2 mt-4" data-id="element-105">
                  Ecosistema Completo
                </h3>
                <p className="text-sm text-gray-400 font-medium mb-6" data-id="element-106">
                  Todo lo que necesitas, sin costes ocultos
                </p>

                <motion.div animate={{
                scale: [1, 1.02, 1]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-6xl sm:text-7xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400 tracking-tighter leading-none mb-8 drop-shadow-lg" data-id="element-107">
                  350€
                </motion.div>

                <div className="w-full h-px bg-white/10 mb-8" data-id="element-108"></div>

                <div className="flex flex-col gap-4 text-left w-full" data-id="element-109">
                  <div className="flex items-center gap-3 text-sm text-gray-300 font-medium" data-id="element-110">
                    <div className="w-6 h-6 rounded-full bg-brand-emerald/20 flex items-center justify-center shrink-0" data-id="element-111">
                      <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-112" />
                    </div>
                    <span data-id="element-113">Página web profesional a medida</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-300 font-medium" data-id="element-114">
                    <div className="w-6 h-6 rounded-full bg-brand-emerald/20 flex items-center justify-center shrink-0" data-id="element-115">
                      <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-116" />
                    </div>
                    <span data-id="element-117">Carta digital interactiva</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-300 font-medium" data-id="element-118">
                    <div className="w-6 h-6 rounded-full bg-brand-emerald/20 flex items-center justify-center shrink-0" data-id="element-119">
                      <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-120" />
                    </div>
                    <span data-id="element-121">Configuración de redes sociales</span>
                  </div>
                </div>
              </motion.div>

              {/* Pago Inicial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col p-6 sm:p-8 rounded-3xl bg-[#111] border border-white/5 hover:border-white/10 transition-all duration-300" data-id="element-122">
                <div className="flex items-center justify-between mb-8" data-id="element-123">
                  <div data-id="element-124">
                    <h4 className="text-white font-bold text-xl mb-1" data-id="element-125">
                      Reserva
                    </h4>
                    <span className="text-sm text-gray-400 font-medium" data-id="element-126">
                      Inicio del proyecto
                    </span>
                  </div>
                  <div className="text-3xl font-black text-white" data-id="element-127">50€</div>
                </div>

                <p className="text-gray-400 font-medium leading-relaxed mb-8" data-id="element-128">
                  Abono anticipado para confirmar tu proyecto. Comenzamos a
                  diseñar y desarrollar el mismo día.
                </p>

                <div className="mt-auto flex flex-col gap-3" data-id="element-129">
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-130">
                    <CheckCircle2 size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-131" />
                    <span data-id="element-132">Inicio inmediato tras la confirmación</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-133">
                    <CheckCircle2 size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-134" />
                    <span data-id="element-135">Primer borrador en 24–48h</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-136">
                    <CheckCircle2 size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-137" />
                    <span data-id="element-138">Comunicación directa continua</span>
                  </div>
                </div>
              </motion.div>

              {/* Pago Final */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-col p-6 sm:p-8 rounded-3xl bg-[#111] border border-white/5 hover:border-brand-emerald/20 transition-all duration-300 relative overflow-hidden group" data-id="element-139">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-emerald/5 blur-[50px] rounded-full pointer-events-none group-hover:bg-brand-emerald/10 transition-colors" data-id="element-140"></div>

                <div className="flex items-center justify-between mb-8 relative z-10" data-id="element-141">
                  <div data-id="element-142">
                    <h4 className="text-white font-bold text-xl mb-1" data-id="element-143">
                      Entrega
                    </h4>
                    <span className="text-sm text-brand-emerald/80 font-medium" data-id="element-144">
                      Satisfacción 100%
                    </span>
                  </div>
                  <div className="text-3xl font-black text-brand-emerald" data-id="element-145">
                    300€
                  </div>
                </div>

                <p className="text-gray-400 font-medium leading-relaxed mb-8 relative z-10" data-id="element-146">
                  Solo pagas cuando estés 100% satisfecho con el resultado
                  final. Sin letra pequeña.
                </p>

                <div className="mt-auto flex flex-col gap-3 relative z-10" data-id="element-147">
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-148">
                    <CheckCircle2 size={16} className="text-brand-emerald/60 shrink-0 mt-0.5" data-id="element-149" />
                    <span data-id="element-150">Revisiones ilimitadas hasta tu aprobación</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-151">
                    <CheckCircle2 size={16} className="text-brand-emerald/60 shrink-0 mt-0.5" data-id="element-152" />
                    <span data-id="element-153">Tú decides cuándo está listo</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-gray-300" data-id="element-154">
                    <CheckCircle2 size={16} className="text-brand-emerald/60 shrink-0 mt-0.5" data-id="element-155" />
                    <span data-id="element-156">Sin compromiso si no te convence</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* CTA Row */}
            <div className="mt-12 flex flex-col items-center justify-center text-center relative z-10" data-id="element-157">
              <Link to="/checkout" className="inline-flex justify-center items-center gap-2 bg-brand-emerald text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-brand-emeraldLight transition-all duration-300 shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] hover:-translate-y-1 group" data-id="element-158">
                Empezar ahora
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-159" />
              </Link>
              <p className="mt-4 text-sm text-gray-400 font-medium" data-id="element-160">
                Sin compromiso · Respuesta en 24h
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}